import { api } from './index'

const BUILDER_CONTENT_API = 'https://cdn.builder.io/api/v3/content'
const WHATS_NEW_MODEL = 'whats-new'
const CHANGELOG_MODEL = 'changelog'
const BUILDER_PUBLIC_API_KEY = process.env.BUILDER_PUBLIC_API_KEY ?? ''

const REQUEST_TIMEOUT_MS = 1500
const DISCO_ORIGIN = 'https://www.disco.ac'
const LOGIN_PAGE_UTM_PARAMS = {
  utm_source: 'login_page',
  utm_medium: 'whats_new_link',
}

// title/seo.description are localized fields in Builder. Fetched directly from
// the `changelog` model, the raw value is keyed by locale (e.g. `Default`) -
// but resolved through a reference (`includeRefs`/`enrich`), Builder returns
// the already-resolved plain string instead. Handle both.
interface BuilderLocalizedValue {
  Default?: string
  [locale: string]: string | undefined
}

type BuilderLocalizedField = BuilderLocalizedValue | string | undefined

export function resolveLocalized(
  value: BuilderLocalizedField,
): string | undefined {
  return typeof value === 'string' ? value : value?.Default
}

interface BuilderChangelogEntry {
  id: string
  firstPublished?: number
  data: {
    title?: BuilderLocalizedField
    featureImage?: string
    url?: string
    seo?: {
      description?: BuilderLocalizedField
    }
  }
}

interface BuilderWhatsNewEntry {
  data: {
    source?: 'Latest Changelog' | 'Specific Changelog' | 'Custom'
    changelogReference?: { value?: BuilderChangelogEntry }
    title?: string
    description?: string
    image?: string
    link?: string
    linkText?: string
  }
}

export interface WhatsNewItem {
  title: string
  description: string
  image?: string
  link?: string
  linkText?: string
}

function withLoginPageUtmParams(url: string): string {
  const parsed = new URL(url)
  for (const [key, value] of Object.entries(LOGIN_PAGE_UTM_PARAMS)) {
    parsed.searchParams.set(key, value)
  }
  return parsed.toString()
}

export const WHATS_NEW_FALLBACK: WhatsNewItem = {
  title: 'Stay up to date with DISCO!',
  description:
    'Learn about new features and improvements as we add them. Check out the DISCO Changelog.',
  link: withLoginPageUtmParams(`${DISCO_ORIGIN}/changelog`),
  linkText: 'Read the Changelog',
}

// Every login-page link is validated against disco.ac before rendering, both
// the Custom mode's editor-provided link and the Changelog-derived link are
// built from fields on the same untrusted marketing CMS. Resolve with
// `new URL(link, DISCO_ORIGIN)` rather than string concatenation or a
// startsWith('/') heuristic - either of those lets a protocol-relative URL
// (`//evil.com`) or a userinfo trick (`https://www.disco.ac@evil.com`)
// resolve to an off-domain host while still looking like a same-origin or
// disco.ac value at a glance.
function resolveDiscoUrl(link: string): URL | null {
  try {
    const url = new URL(link, DISCO_ORIGIN)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    if (url.hostname !== 'disco.ac' && !url.hostname.endsWith('.disco.ac')) {
      return null
    }
    return url
  } catch {
    return null
  }
}

export function fromChangelogEntry(
  entry: BuilderChangelogEntry,
): WhatsNewItem | null {
  const title = resolveLocalized(entry.data.title)
  const description = resolveLocalized(entry.data.seo?.description)
  if (!title || !description) return null

  const resolvedUrl = entry.data.url ? resolveDiscoUrl(entry.data.url) : null

  return {
    title,
    description,
    image: entry.data.featureImage,
    link: resolvedUrl
      ? withLoginPageUtmParams(resolvedUrl.toString())
      : undefined,
    linkText: 'Learn more',
  }
}

export function fromCustomEntry(
  entry: BuilderWhatsNewEntry,
): WhatsNewItem | null {
  const { title, description, image, link, linkText } = entry.data
  if (!title || !description) return null

  // link/linkText travel together - only show the link paragraph if both are
  // set and the link resolves to disco.ac; used as entered (no UTM append)
  // since this is an editor-provided URL, not a resolved Changelog post
  const hasLink = Boolean(link && linkText && resolveDiscoUrl(link))

  return {
    title,
    description,
    image,
    link: hasLink ? link : undefined,
    linkText: hasLink ? linkText : undefined,
  }
}

async function fetchLatestChangelogItem(
  signal: AbortSignal,
): Promise<WhatsNewItem | null> {
  const params = new URLSearchParams({
    apiKey: BUILDER_PUBLIC_API_KEY,
    limit: '100',
  })

  const response = await fetch(
    `${BUILDER_CONTENT_API}/${CHANGELOG_MODEL}?${params.toString()}`,
    { signal },
  )
  if (!response.ok) return null

  const { results }: { results: BuilderChangelogEntry[] } =
    await response.json()

  // Builder's server-side sort params don't sort as documented - sort client-side
  const sorted = [...results].sort(
    (a, b) => (b.firstPublished ?? 0) - (a.firstPublished ?? 0),
  )

  for (const entry of sorted) {
    const item = fromChangelogEntry(entry)
    if (item) return item
  }
  return null
}

/**
 * Resolves the login page's What's New content from Builder's `whats-new`
 * singleton, honouring its `source` mode (Custom / Specific Changelog /
 * Latest Changelog). Never throws - always resolves to a displayable item,
 * falling back to static content on timeout, a missing entry, or content
 * that doesn't meet the panel's minimum requirements.
 */
export async function getLoginWhatsNew(): Promise<WhatsNewItem> {
  const signal = AbortSignal.timeout(REQUEST_TIMEOUT_MS)

  try {
    const params = new URLSearchParams({
      apiKey: BUILDER_PUBLIC_API_KEY,
      includeRefs: 'true',
      enrich: 'true',
      limit: '1',
    })

    const response = await fetch(
      `${BUILDER_CONTENT_API}/${WHATS_NEW_MODEL}?${params.toString()}`,
      { signal },
    )
    if (!response.ok) return WHATS_NEW_FALLBACK

    const { results }: { results: BuilderWhatsNewEntry[] } =
      await response.json()
    const whatsNew = results[0]
    if (!whatsNew) return WHATS_NEW_FALLBACK

    switch (whatsNew.data.source) {
      case 'Custom':
        return fromCustomEntry(whatsNew) ?? WHATS_NEW_FALLBACK

      case 'Specific Changelog': {
        const referenced = whatsNew.data.changelogReference?.value
        const item = referenced ? fromChangelogEntry(referenced) : null
        return item ?? WHATS_NEW_FALLBACK
      }

      case 'Latest Changelog':
      default: {
        const item = await fetchLatestChangelogItem(signal)
        return item ?? WHATS_NEW_FALLBACK
      }
    }
  } catch {
    // Timeout (AbortError) or network error - fall back rather than surface
    // an error state on the login page
    return WHATS_NEW_FALLBACK
  }
}

export const whatsNewApi = api
  .enhanceEndpoints({ addTagTypes: ['WhatsNew'] })
  .injectEndpoints({
    endpoints: build => ({
      getLoginWhatsNew: build.query<WhatsNewItem, void>({
        queryFn: async () => ({ data: await getLoginWhatsNew() }),
        providesTags: ['WhatsNew'],
      }),
    }),
  })

export const { useGetLoginWhatsNewQuery } = whatsNewApi
